import dayjs from 'dayjs';
import type { FormInstance } from 'antd';
import {
    getCredBasicInfo,
    saveCredBasicInfo,
    getCredLicenses,
    saveCredLicenses,
    getCredPractice,
    saveCredPractice,
    getCredMalpractice,
    saveCredMalpractice,
    getCredBackground,
    saveCredBackground,
    getCredReferences,
    saveCredReferences,
    getCredVerification,
    saveCredVerification,
    getCredAllDetail,
} from 'api/credentialing';
import type { ProviderCredentialingWizardDetailVo } from 'api/credentialing';
import { removeHMSInUTCTime } from 'utils/common';
import { DISCLOSURES, SPECIALTY_OTHERS } from './constants';
import { CRED_STEP_KEYS, type CredStepKey } from './steps';

// 表单值直接对应接口 DTO；仅做通用日期序列化，不做字段名映射。
// 后端日期字段是 java.time.LocalDate，一律要求 'YYYY-MM-DD'。
// 两类日期控件：antd DatePicker → dayjs；BirthdayInput（birthDate）→ 'MM/DD/YYYY' 字符串。
const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const MDY_RE = /^\d{2}\/\d{2}\/\d{4}$/;
export const serializeDates = (v: any): any => {
    if (v == null) return v;
    if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
    // BirthdayInput 输出 'MM/DD/YYYY'，转成后端要求的 ISO 'YYYY-MM-DD'
    if (typeof v === 'string' && MDY_RE.test(v)) {
        const [mm, dd, yyyy] = v.split('/');
        return `${yyyy}-${mm}-${dd}`;
    }
    if (Array.isArray(v)) return v.map(serializeDates);
    if (typeof v === 'object') {
        return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, serializeDates(val)]));
    }
    return v;
};
export const hydrateDates = (v: any, key?: string): any => {
    if (v == null) return v;
    if (typeof v === 'string' && DATE_RE.test(v)) {
        // birthDate 回填给 BirthdayInput，需要 'MM/DD/YYYY' 字符串（不是 dayjs）
        if (key === 'birthDate') {
            const [yyyy, mm, dd] = v.slice(0, 10).split('-');
            return `${mm}/${dd}/${yyyy}`;
        }
        // 后端（uniprofile 同源数据）可能返回带时区的 ISO 串，如 '2027-10-01T00:00:00.000Z'：
        // 直接 dayjs() 会按浏览器本地时区换算，美区用户会看到少一天。先截掉时间部分只保留日历日
        // （与 utils/common 的 removeHMSInUTCTime 及 data/provider 的 license 回填逻辑一致）
        return dayjs(removeHMSInUTCTime(v));
    }
    if (Array.isArray(v)) return v.map((item) => hydrateDates(item));
    if (typeof v === 'object') {
        return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, hydrateDates(val, k)]));
    }
    return v;
};

// license/board/dea 条目是否为空：除了 id / licenseType（恒为 null）外没有任何有值字段
// 表单默认给 practiceLicenses / boardCertifications 各塞一条空 {}，未填写时不能作为空对象提交
// （后端对 [{}] / [{licenseType:null}] 这类空行会 500），需过滤成空数组 []
const isBlankLicenseEntry = (o: any) =>
    !o ||
    Object.entries(o).every(
        ([k, val]) =>
            k === 'id' || k === 'licenseType' || val == null || val === '' || (Array.isArray(val) && val.length === 0),
    );

// 执业地址条目是否为空行：四个地址字段与服务类型都没填（id 之类的元数据不算）
const isBlankAddressEntry = (a: any) =>
    !a || (!a.practiceAddress && !a.practiceCity && !a.practiceState && !a.practiceZip && !a.careDelivery);

// experience / education 条目是否为空行：除 id 与 currently（在职 / 在读，只是修饰）外没有任何有值字段。
// 表单给这两个列表各塞一条空 {} 打底，而 Background 步的 Next 只保存不校验，未填写时会把 [{}]
// 发给后端，后端按「有一条经历」继续走保存逻辑然后报错（前端只显示通用错误页）。
// 判定用「全空才算空行」而不是只看 schoolName / organizationName：只填了 Major 或只填了起止日期
// 的行也是用户填的内容，不能静默丢掉；反过来，内容被清空但还带着 id 的行必须丢掉，
// 否则等于把一条空记录原样发回后端。
const isBlankBackgroundEntry = (o: any) =>
    !o ||
    Object.entries(o).every(
        ([k, val]) =>
            k === 'id' || k === 'currently' || val == null || val === '' || (Array.isArray(val) && val.length === 0),
    );

// 提交前统一出参处理：日期序列化 + credential 数组转回逗号串
export const stepValuesToDto = (key: CredStepKey, values: any): any => {
    const body = serializeDates(values);
    if (key === 'identity' && Array.isArray(body?.credential)) body.credential = body.credential.join(',');
    if (key === 'licenses') {
        // 执业许可：过滤空行后，用户填写的许可类型存入 type，licenseType 恒为 null（与 NewUniProfile 一致）
        if (Array.isArray(body?.practiceLicenses)) {
            body.practiceLicenses = body.practiceLicenses
                .filter((lic: any) => !isBlankLicenseEntry(lic))
                .map((lic: any) => ({ ...lic, licenseType: null }));
        }
        // board certifications / DEA licenses：空行同样过滤成 []，避免提交 [{}]
        if (Array.isArray(body?.boardCertifications)) {
            body.boardCertifications = body.boardCertifications.filter((b: any) => !isBlankLicenseEntry(b));
        }
        if (Array.isArray(body?.deaLicenses)) {
            body.deaLicenses = body.deaLicenses.filter((d: any) => !isBlankLicenseEntry(d));
        }
    }
    // Specialty 的「Others」不入库：用输入框里填写的实际专科替换掉它（没填就直接丢掉），
    // specialtyOfferedOther 只是辅助输入的表单字段，不属于接口 DTO
    if (key === 'practice') {
        if (Array.isArray(body?.specialtyOffered)) {
            const other = String(body.specialtyOfferedOther ?? '').trim();
            body.specialtyOffered = body.specialtyOffered.flatMap((sp: any) => {
                if (sp !== SPECIALTY_OTHERS) return [sp];
                return other ? [other] : [];
            });
        }
        delete body.specialtyOfferedOther;
        // 多地址：完全空白的行不提交（后端对 [{}] 这类空行会炸）
        if (Array.isArray(body?.addressList)) {
            body.addressList = body.addressList.filter((a: any) => !isBlankAddressEntry(a));
        }
    }
    if (key === 'experience') {
        if (Array.isArray(body?.providerExperienceList)) {
            body.providerExperienceList = body.providerExperienceList
                .filter((w: any) => !isBlankBackgroundEntry(w))
                // 在职中（currently=true）不提交离职日期：表单里之所以还留着旧值，只是为了取消勾选后能还原，
                // 见 StepExperience 的 End date（勾选时展示 Present，不绑定表单）
                .map((w: any) => (w?.currently ? { ...w, endDate: null } : w));
        }
        if (Array.isArray(body?.providerEducationList)) {
            body.providerEducationList = body.providerEducationList.filter((e: any) => !isBlankBackgroundEntry(e));
        }
    }
    return body;
};

// 每个向导步骤对应的读取/保存接口；references 的 body 为数组，直接提交该字段。
// get/save 都可选透传 overrideProviderId（group owner 代填 provider 时用）。
export type OverrideProviderId = number | string | undefined;
export const STEP_API: Record<
    CredStepKey,
    { get: (o?: OverrideProviderId) => Promise<any>; save: (v: any, o?: OverrideProviderId) => Promise<any> }
> = {
    identity: { get: getCredBasicInfo, save: saveCredBasicInfo },
    licenses: { get: getCredLicenses, save: saveCredLicenses },
    practice: { get: getCredPractice, save: saveCredPractice },
    malpractice: { get: getCredMalpractice, save: saveCredMalpractice },
    experience: { get: getCredBackground, save: saveCredBackground },
    references: { get: getCredReferences, save: saveCredReferences },
    final: { get: getCredVerification, save: saveCredVerification },
};

// 每步提交只发送该步骤接口 DTO 定义的字段（表单字段即接口字段，无需映射转换）；
// 由于所有步骤共用同一个 Form，必须按字段名裁剪，避免把其它步骤的字段一起提交
export const STEP_FIELDS: Record<CredStepKey, string[]> = {
    identity: [
        'firstName',
        'middleName',
        'lastName',
        'formerName',
        'birthDate',
        'gender',
        'npi',
        'billingNpi',
        'credential',
        'citizenship',
        'workAuthorizationType',
        'email',
        'tel',
        'fax',
        'contactAddress',
        'mailingAddress',
    ],
    licenses: [
        'practiceLicenses',
        'boardCertifications',
        'deaLicenses',
        'cdsCertificate',
        'multistateAuthorities',
        'identification',
    ],
    practice: [
        'practiceName',
        'startDate',
        'monthlyVisits',
        'patientPayerMix',
        'practicePhone',
        'practiceType',
        'addressList',
        'specialtyOffered',
        // 仅用于承载 Others 的输入内容，提交前会被合并进 specialtyOffered 并从 body 里删掉
        'specialtyOfferedOther',
        'careDelivery',
        'ehrSystems',
        'ehrSystemsOther',
    ],
    malpractice: [
        'carrier',
        'licenseNumber',
        'effectiveDate',
        'expireDate',
        'perOccurrenceCents',
        'aggregateCents',
        'url',
    ],
    experience: ['providerExperienceList', 'providerEducationList'],
    references: ['references'],
    final: [
        'hasCaqhAccount',
        'caqhUsername',
        'caqhPassword',
        'ssn',
        'legalTaxName',
        'taxId',
        'businessEntityType',
        'w9FormUrl',
        'disclosures',
        'hasFacilityPrivileges',
        'facilityName',
    ],
};

// 把某个 step 接口返回的 dto 归一成可灌进 form 的 patch（与向导 loadStep 的合并逻辑一致）：
// references 的 body 是数组，需包一层；final 的 disclosures 按 questionKey 合并回完整 6 题。
export const stepDtoToPatch = (key: CredStepKey, dto: any): Record<string, any> => {
    if (dto == null) return {};
    const patch = key === 'references' ? { references: dto } : { ...dto };
    // credential 后端是逗号分隔字符串，表单是多选数组（与 NewUniProfile 等处一致）
    if (key === 'identity') {
        patch.credential = typeof patch.credential === 'string' ? patch.credential.split(',').filter(Boolean) : [];
        // 后端未返回 citizenship 时删掉空值，保留 StepIdentity 的默认 initialValue="US citizen"（不要用 null 覆盖）
        if (patch.citizenship == null || patch.citizenship === '') delete patch.citizenship;
        // 后端可能返回只剩元数据骨架（四个地址子字段全空）的 mailingAddress 记录，
        // 若保留会被校验按「任一子字段有值→四项全必填」误判勾选。四项全空才视为无邮寄地址；
        // 部分有值（如草稿只存了 state）算勾选——StepIdentity 直读 store 会自动展开，让用户补全。
        const ma = patch.mailingAddress;
        if (ma && !ma.practiceAddress && !ma.practiceCity && !ma.practiceState && !ma.practiceZip)
            delete patch.mailingAddress;
    }
    // 后端可能返回少于 3 条 reference，Form.List 会因 setFieldsValue 缩短为实际返回条数；
    // 补齐至 MIN_REFERENCES=3 条空条目，与 StepReferences initialValue 保持一致
    if (key === 'references') {
        const refs: any[] = Array.isArray(patch.references) ? patch.references : [];
        while (refs.length < 3) refs.push({});
        patch.references = refs;
    }
    // 后端可能返回空数组，Form.List 会渲染 0 行导致「必填却无输入行」；归一成至少一行空条目
    if (key === 'licenses') {
        if (!Array.isArray(patch.practiceLicenses) || patch.practiceLicenses.length === 0)
            patch.practiceLicenses = [{}];
        if (!Array.isArray(patch.boardCertifications) || patch.boardCertifications.length === 0)
            patch.boardCertifications = [{}];
        // deaLicenses 同理：接口返回 [] 时 Form.List 的 initialValue 不生效，展开 DEA 区块会一行输入都没有，
        // 只剩「+ Add another DEA license」。空行在提交时由 isBlankLicenseEntry 过滤掉，不会写脏数据
        if (!Array.isArray(patch.deaLicenses) || patch.deaLicenses.length === 0) patch.deaLicenses = [{}];
    }
    if (key === 'final') {
        patch.disclosures = DISCLOSURES.map(
            (d) => (dto.disclosures || []).find((x: any) => x.questionKey === d.id) || { questionKey: d.id },
        );
    }
    if (key === 'references') {
        // 后端返回的数组会覆盖 Form.List 的 initialValue；归一成至少 3 行（前 3 条为最低要求且不可删除）
        const refs = Array.isArray(patch.references) ? patch.references : [];
        patch.references = refs.length >= 3 ? refs : [...refs, ...Array.from({ length: 3 - refs.length }, () => ({}))];
    }
    if (key === 'experience') {
        // 后端返回空数组会覆盖 Form.List 的 initialValue 导致 0 行；归一成至少一行，默认展开一条
        if (!Array.isArray(patch.providerExperienceList) || patch.providerExperienceList.length === 0)
            patch.providerExperienceList = [{}];
        if (!Array.isArray(patch.providerEducationList) || patch.providerEducationList.length === 0)
            patch.providerEducationList = [{}];
    }
    if (key === 'practice') {
        // 后端未返回 practiceType 时删掉空值，保留 StepPractice 按入口 NPI 类型的默认填充（不要用 null 覆盖）
        if (patch.practiceType == null || patch.practiceType === '') delete patch.practiceType;
        // 执业地址归一成数组：后端已改用 addressList，旧回包（或还没迁移的聚合接口）
        // 只有单个 practiceAddress，兜底包成一条；都没有时给一条空行，
        // 否则 Form.List 会渲染 0 行（必填却无输入框）
        const addrs: any[] = Array.isArray(patch.addressList)
            ? patch.addressList.filter((a: any) => a && typeof a === 'object')
            : [];
        if (addrs.length === 0 && patch.practiceAddress && typeof patch.practiceAddress === 'object') {
            addrs.push(patch.practiceAddress);
        }
        patch.addressList = addrs.length ? addrs : [{}];
        // 旧字段不再进表单，避免和数组第一条出现两份真相
        delete patch.practiceAddress;
        // specialtyOffered 多选：过滤空项；null/空数组必须归一为 undefined —— antd 多选拿到 null 会渲染出一个空 chip（只剩「×」）
        const spec = Array.isArray(patch.specialtyOffered)
            ? patch.specialtyOffered.filter((x: any) => x != null && x !== '')
            : [];
        patch.specialtyOffered = spec.length ? spec : undefined;
        // ehrSystems 多选：与 specialtyOffered 同处理，过滤空项；空数组/null 归一为 undefined
        const ehr = Array.isArray(patch.ehrSystems) ? patch.ehrSystems.filter((x: any) => x != null && x !== '') : [];
        patch.ehrSystems = ehr.length ? ehr : undefined;
        // careDelivery 单选：空字符串/null 归一为 undefined，避免展示异常
        if (patch.careDelivery === '' || patch.careDelivery == null) patch.careDelivery = undefined;
    }
    return patch;
};

// 保存某步骤：POST 成功后立刻重新拉一次该步数据回填表单。
// 数组型条目（执业地址 addressList / 许可 / references…）新增行的 id 是后端分配的，
// 不回填的话表单里那几行始终没有 id，下一次保存会被后端当成新记录再插一遍
// （向导 Back 回上一步不会重新拉数据，Review / Settings 里连点两次 Save 同理）。
// 返回是否保存成功；回填失败不影响「已保存」的结论。
export const saveStepValues = async (
    key: CredStepKey,
    form: FormInstance,
    providerId?: OverrideProviderId,
): Promise<boolean> => {
    const body = stepValuesToDto(key, form.getFieldsValue(STEP_FIELDS[key]));
    const res: any = await STEP_API[key].save(key === 'references' ? body.references || [] : body, providerId);
    if (res?.error) return false;
    try {
        const fresh: any = await STEP_API[key].get(providerId);
        const dto = fresh?.data?.data;
        if (dto != null) form.setFieldsValue(hydrateDates(stepDtoToPatch(key, dto)));
    } catch (e) {
        // 忽略：回填失败只影响「新增条目下次保存会重复插入」，不该让已成功的保存显示为失败
    }
    return true;
};

// 一次性拉齐某个 provider 的全部 7 步数据，合并成一个扁平 values 对象（等价于 solo 向导逐步攒进 form 的结果）。
// 用于 group review：跨页面进来时 form 为空，需主动按 providerId 拉齐每个 provider 的数据。best-effort，
// 单步接口失败不阻塞其它步骤。
export const fetchProviderCredValues = async (providerId?: OverrideProviderId): Promise<Record<string, any>> => {
    const patches = await Promise.all(
        CRED_STEP_KEYS.map(async (key) => {
            try {
                const res: any = await STEP_API[key].get(providerId);
                return stepDtoToPatch(key, res?.data?.data);
            } catch (e) {
                return {};
            }
        }),
    );
    return hydrateDates(Object.assign({}, ...patches));
};

// step key → aggregate 返回体里对应 body 的字段名
const AGGREGATE_STEP_FIELD: Record<CredStepKey, keyof ProviderCredentialingWizardDetailVo> = {
    identity: 'basicInfo',
    licenses: 'licenses',
    practice: 'practice',
    malpractice: 'malpractice',
    experience: 'background',
    references: 'references',
    final: 'verification',
};

export const wizardDetailToValues = (vo?: ProviderCredentialingWizardDetailVo | null): Record<string, any> => {
    if (!vo) return {};
    const patches = CRED_STEP_KEYS.map((key) => stepDtoToPatch(key, (vo as any)[AGGREGATE_STEP_FIELD[key]]));
    return hydrateDates(Object.assign({}, ...patches));
};

// 一次请求拿齐 7 步数据；没有 panel id 或接口失败时回退到逐步拉取
export const fetchProviderCredValuesByPanel = async (
    credentialingPayerId?: number,
    providerId?: OverrideProviderId,
): Promise<Record<string, any>> => {
    if (!credentialingPayerId) return fetchProviderCredValues(providerId);
    try {
        const res: any = await getCredAllDetail(credentialingPayerId, providerId as number | string | undefined);
        if (res?.error) throw new Error(res.error);
        const vo = res?.data?.data;
        if (!vo) throw new Error('empty all-detail payload');
        return wizardDetailToValues(vo);
    } catch (e) {
        return fetchProviderCredValues(providerId);
    }
};
